import React from 'react';

export const CARD = 'bg-white rounded-xl border border-gray-200 shadow-sm';

export const BUTTON =
  'inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed';
export const PRIMARY = `${BUTTON} bg-claret-500 text-white hover:bg-claret-600`;
export const SECONDARY = `${BUTTON} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
export const INPUT =
  'rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-claret-500';

export function Card({ title, aside, children, className = '' }) {
  return (
    <section className={`${CARD} p-5 ${className}`}>
      {(title || aside) && (
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          {title && (
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          )}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

export function Stat({ label, value, hint }) {
  return (
    <div className={`${CARD} p-4`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

const BADGES = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-green-50 text-green-800 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  queued: 'bg-green-50 text-green-800 border-green-200',
  sent: 'bg-green-50 text-green-800 border-green-200',
  done: 'bg-green-50 text-green-800 border-green-200',
  running: 'bg-blue-50 text-blue-800 border-blue-200',
  dry_run: 'bg-gray-50 text-gray-700 border-gray-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  test: 'bg-purple-50 text-purple-800 border-purple-200',
};

const LABELS = {
  pending: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  queued: 'Sent',
  sent: 'Sent',
  done: 'Done',
  running: 'Running',
  dry_run: 'Dry run',
  failed: 'Failed',
  test: 'Test',
};

export function Badge({ status, label }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${
        BADGES[status] ?? 'bg-gray-50 text-gray-700 border-gray-200'
      }`}
    >
      {label ?? LABELS[status] ?? status}
    </span>
  );
}

export const percent = (value) =>
  value || value === 0 ? `${Math.round(value * 1000) / 10}%` : '—';

export const count = (value) => (value ?? 0).toLocaleString();

export const date = (value, withTime = false) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

export const fullName = (person) =>
  [person?.first_name, person?.last_name].filter(Boolean).join(' ') ||
  person?.email ||
  'Unknown';

export function Empty({ children }) {
  return (
    <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
      {children}
    </p>
  );
}

export function Pager({ total, limit, offset, onChange }) {
  if (!total || total <= limit) return null;
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.ceil(total / limit);
  return (
    <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
      <span>
        {offset + 1}–{Math.min(offset + limit, total)} of {count(total)}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          className={SECONDARY}
          disabled={page <= 1}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </button>
        <button
          type="button"
          className={SECONDARY}
          disabled={page >= pages}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
